<template>
  <i
    class="tm-overlay-menu"
    :class="{
      // not v-if - cause of fade animation
      v: isRelevant,
    }"
    :style="styleObj"
  >
    <i class="-shadow">
      <i class="-balloon">
        <i
          class="-button"
          title="Layout"
          @mousedown.left.stop="$_noop"
          @mouseup.left.stop="$_menuEditOverlay"
        >
          <i class="wticon wticon-pencil3" />
        </i>

        <i class="-divider" />

        <template v-if="isRelevant && isVideoFragment">
          <i
            class="-button"
            title="Re-Trim"
            @mousedown.left.stop="$_noop"
            @mouseup.left.stop="$_menuEditSegment"
          >
            <i class="wticon wticon-Cut2" />
          </i>
        </template>

        <i
          class="-button"
          title="Duplicate"
          :class="{
            '-disabled': disableDuplicate,
          }"
          @mousedown.left.stop="$_noop"
          @mouseup.left.stop="$_menuDuplicateOverlay"
        >
          <i class="wticon wticon-duplicate" />
        </i>

        <i
          class="-button"
          title="Delete"
          @mousedown.left.stop="$_noop"
          @mouseup.left.stop="$_menuRemoveOverlay"
        >
          <i class="wticon wticon-trash2" />
        </i>
      </i>
    </i>
  </i>
</template>

<script>
import _ from 'lodash-extended';
import { mapActions, mapGetters, mapState } from 'vuex';
import tmBus from '../../api/tmBus';

export default {
  name: 'tm-overlay-menu',

  props: {
    overlay: { type: Object, required: true },
    pipa: { type: Object, required: true },
    overlayRectangles: { type: Array, required: true },
    isSelected: { type: Boolean, required: true },
    hoveredRectangle: { type: Object, required: false, default: null },
  },

  computed: {
    ...mapGetters('timemachine', ['isDndMode']),
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      cssVariables: (state) => state.cssVariables,
      trackLayerVisual: (state) => state.trackLayer.visual,
      axis: (state) => state.viewOffsets.axis,
      sceneTrack: (state) => state.sceneEscort.sceneTrack,
    }),

    isRelevant() {
      return this.$props.isSelected && !this.isDndMode;
    },

    isVideoFragment() {
      return (
        this.overlay &&
        (app.assetType.isFRAGMENT(this.overlay) ||
          app.assetType.isSOCIALFRAGMENT(this.overlay))
      );
    },

    disableDuplicate() {
      return !this.$props.pipa.canDuplicate(this.$props.overlay);
    },

    styleObj() {
      if (!this.$props.hoveredRectangle || this.isDndMode) {
        return;
      }

      const point = this.$props.hoveredRectangle.p1.clone();
      const rightEdge = this.axis.width - this.cssVariables.pxAxisSidePadding;
      const sceneFirstRectangle = this.sceneTrack?.rectangles[0];
      const isAtFirstRow =
        this.$props.hoveredRectangle === this.$props.overlayRectangles[0] &&
        sceneFirstRectangle?.includesVertically(
          this.$props.hoveredRectangle.p1
        );

      if (isAtFirstRow) {
        const menuMarginLeft = 5;
        const sceneMenuRightX =
          menuMarginLeft +
          sceneFirstRectangle.p1.x +
          this.cssVariables.pxVisualSceneMenuW;
        const overlap = sceneMenuRightX - point.x;

        if (overlap > 0) {
          point.shift(overlap, 0);
        }
      }

      const menuRightX = point.x + this.cssVariables.pxOverlayMenuW;

      if (menuRightX > rightEdge) {
        const shiftLeft = menuRightX - rightEdge;
        point.shift(-shiftLeft, 0);
      }

      return {
        transform: `translate3d(${point.x}px, ${point.y}px, 0)`,
      };
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'SCENE_EDIT_MODE_APPLY',
      'DUPLICATE_VISUAL_OVERLAY',
      'REMOVE_VISUAL_OVERLAY',
      'SELECT_OVERLAY',
    ]),

    $_noop(e) {},

    $_menuEditOverlay: _.debounce(
      function (e) {
        this.SCENE_EDIT_MODE_APPLY({ keepItSelected: false }).then(
          ({ sceneTrack }) => {
            tmBus.$emit('callbackApplySceneEdit', {
              e,
              asset: sceneTrack.data,
              analyticsData: { confirm: 'v', using: 'edit' },
            });
            tmBus.$emit('callbackEditAsset', {
              asset: sceneTrack.data,
              overlayGuid: this.$props.overlay.guid,
            });
          }
        );
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuDuplicateOverlay: _.debounce(
      function (e) {
        this.DUPLICATE_VISUAL_OVERLAY({ overlay: this.$props.overlay }).then(
          ({ actingPipa, newOverlay }) => {
            tmBus.$emit('callbackSceneUpdate', {
              e,
              scene: actingPipa.scene,
              reflectUsage: false,
            });

            this.SELECT_OVERLAY({ overlay: newOverlay });
          }
        );
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuEditSegment: _.debounce(
      function (e) {
        tmBus.$emit('callbackEditSegment', {
          asset: { data: this.$props.overlay },
          overlayGuid: this.$props.overlay.guid,
          pipa: this.$props.pipa,
        });
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuRemoveOverlay: _.debounce(
      function (e) {
        this.REMOVE_VISUAL_OVERLAY({ overlay: this.$props.overlay }).then(
          ({ actingPipa }) => {
            tmBus.$emit('callbackSceneUpdate', {
              e,
              scene: actingPipa.scene,
              reflectUsage: true,
            });
          }
        );
      },
      256,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.tm-overlay > .tm-overlay-menu {
  --menuHeight: 40px;
  --buttonWidth: 50px;

  position: absolute;
  top: 0;
  z-index: var(--tm-zOverlayMenu);
  margin-top: -14px;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in;
  white-space: nowrap;
  display: flex;

  &.v {
    visibility: visible;
    opacity: 1;
  }

  > .-shadow {
    box-shadow: -1px 1px 10px $color-black-l;
    display: flex;

    > .-balloon {
      display: flex;
      flex-direction: row;
      align-content: center;
      align-items: center;
      width: var(--tm-pxOverlayMenuW);
      height: var(--menuHeight);
      background-color: #fff;
      border: 1px solid #ccc;

      > .-divider {
        border-right: 1px solid $color-black-l;
        height: 100%;
        width: 0;
      }

      > .-button {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        height: calc(var(--menuHeight) - 2px);
        width: var(--buttonWidth);
        color: $color-normal;

        &:hover {
          background-color: $color-black-l2;
        }

        &.-disabled {
          pointer-events: none;
          .wticon {
            color: $color-disabled;
          }
        }

        .wticon {
          font-size: 20px;
        }

        &.-tune1 {
          .wticon {
            transform: translate(0.5px, 1px);
          }
        }
      }
    }
  }
}
</style>
